import { useState, useMemo, ReactNode } from 'react'
import { MoreHorizontal } from 'lucide-react'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'

// Admin Panel Redesign — thin wrapper over the Table primitive adding row
// multi-select + a per-row overflow menu, generalized so every section
// (Users, Organizations, Logs, Audit, Subscriptions) can drop 9 buttons/row
// down to 2-3 primary actions + a "..." menu, and get bulk operations for free.

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  className?: string
}

export interface DataTableAction<T> {
  label: string
  onClick: (row: T) => void
  destructive?: boolean
  icon?: ReactNode
  hidden?: (row: T) => boolean
}

export interface DataTableBulkAction<T> {
  label: string
  onClick: (selected: T[]) => void
  destructive?: boolean
  icon?: ReactNode
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  getRowId: (row: T) => string
  rowActions?: DataTableAction<T>[]
  bulkActions?: DataTableBulkAction<T>[]
  emptyMessage?: string
  isLoading?: boolean
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  rowActions,
  bulkActions,
  emptyMessage = 'No results.',
  isLoading = false,
}: DataTableProps<T>) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const selectable = !!bulkActions?.length

  const rowsById = useMemo(() => {
    const map = new Map<string, T>()
    rows.forEach((r) => map.set(getRowId(r), r))
    return map
  }, [rows, getRowId])

  const selectedRows = useMemo(
    () => Array.from(selected).map((id) => rowsById.get(id)).filter(Boolean) as T[],
    [selected, rowsById]
  )

  const allSelected = rows.length > 0 && selected.size === rows.length
  const someSelected = selected.size > 0 && !allSelected

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(rows.map(getRowId)))
    }
  }

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-2">
      {selectable && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm text-muted-foreground">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            {bulkActions!.map((action) => (
              <Button
                key={action.label}
                size="sm"
                variant={action.destructive ? 'destructive' : 'outline'}
                onClick={() => action.onClick(selectedRows)}
              >
                {action.icon}
                {action.label}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
              {!!rowActions?.length && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (rowActions?.length ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (rowActions?.length ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = getRowId(row)
                const visibleActions = rowActions?.filter((a) => !a.hidden?.(row))
                return (
                  <TableRow key={id} data-state={selected.has(id) ? 'selected' : undefined}>
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          checked={selected.has(id)}
                          onCheckedChange={() => toggleRow(id)}
                          aria-label="Select row"
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key} className={col.className}>
                        {col.render(row)}
                      </TableCell>
                    ))}
                    {!!rowActions?.length && (
                      <TableCell>
                        {!!visibleActions?.length && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {visibleActions.map((action) => (
                                <DropdownMenuItem
                                  key={action.label}
                                  destructive={action.destructive}
                                  onClick={() => action.onClick(row)}
                                >
                                  {action.icon}
                                  {action.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
